import {omit, capitalize, isNull} from 'lodash-es';
import ObjectUtils from '@spothero/utils/object';

import {spotheroGet, spotheroPost} from 'api';
import dayjs from 'utils/dayjs';
import {sendSentryException} from 'utils/error-handling';
import {convertKeysToSnakeCase} from 'utils/format-strings';
import {formatPrice} from 'utils/number-format';
import {
    FLEX_RATES_CONFIG_DURATION_IN_MIN,
    generateRateSegmentDescription,
} from 'pages/rates/flex-rates/utils/utils';

/** @module api/rates/flex-rates */

/**
 * Formats the rate bands from the flex rates response
 * @param {TFlexRateBand[]} rateBands
 * @return {TFlexRateFormattedRateBand[]}
 *
 * @memberof module:api/rates/flex-rates/dto
 */
const formatRateBands = (rateBands) => {
    const formattedRateBands = rateBands.reduce((reformattedData, rateBand) => {
        const {duration, daysOfWeek, rate} = rateBand;

        // first we check if the duration exists in the reformattedData object
        if (!reformattedData[duration]) {
            reformattedData[duration] = {duration};
        }

        // then we get the days of the week the rate band applies to and key the respective rate by day
        daysOfWeek.forEach((day) => {
            reformattedData[duration][day.toLowerCase()] = formatPrice(rate);
        });

        return reformattedData;
    }, {});

    return Object.values(formattedRateBands);
};

/**
 * Formats the rate segments from the flex rates response
 * @param {TFlexRateSegment[]|TFlexRateTriggeredSegment[]} rateSegments
 * @return {TFlexRateFormattedRateSegment[] }
 *
 * @memberof module:api/rates/flex-rates/dto
 */
const formatRateSegments = (rateSegments) => {
    const formattedRateSegments = rateSegments.map((rateSegment) => {
        return {
            ...rateSegment,
            minRate: formatPrice(rateSegment.minRate),
            maxRate: formatPrice(rateSegment.maxRate),
            description: generateRateSegmentDescription(rateSegment),
        };
    });

    return formattedRateSegments;
};

/**
 * Formats the flex rates response
 * @param {TFlexRatesResponse} response
 * @return {TFlexRateResponseData}
 *
 * @memberof module:api/rates/flex-rates/dto
 */
const formatFlexRatesResponse = (response) => {
    const formattedResponse = ObjectUtils.camelizeKeys(response);

    const data = {
        ...formattedResponse,
        config: {
            ...formattedResponse.config,
            interpolation:
                formattedResponse.config.interpolation.algorithm.toLowerCase(),
            rateBands: formatRateBands(formattedResponse.config.rateBands),
            rateSegments: formatRateSegments(
                formattedResponse.config.rateSegments
            ),
            rateTriggeredSegments: formatRateSegments(
                formattedResponse.config.rateTriggeredSegments
            ),
        },
    };

    return data;
};

/**
 * Gets the flex rates for a rule group
 * @param {number} ruleGroupId - The id of the rule group
 * @return {Promise<TFlexRateResponseData>}
 *
 * @throws {Error}
 *
 * @see '/api/v1/facilities/rates/rule-groups/{ruleGroupId}/flex-rates/'
 * @function
 */
export const getFlexRates = async (ruleGroupId) => {
    try {
        const response = await spotheroGet(
            `/facilities/rates/rule-groups/${ruleGroupId}/flex-rates/`
        );

        const formattedResponse = formatFlexRatesResponse(response?.data?.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats the rate bands request by grouping days with the same rate and formatting the rate value.
 *
 * @param {TFlexRateFormattedRateBand[]} rateBands - The rate bands data to be formatted.
 * @returns {TFlexRateBand[]} The formatted rate bands data for API request.
 *
 * @memberof module:api/rates/flex-rates/dto
 * @function formatRateBandsRequest
 */
const formatRateBandsRequest = (rateBands) => {
    const formattedRateBands = rateBands.reduce((arr, rateBand) => {
        const {duration, ...daysWithRate} = rateBand;
        // Create an object to group days by rate
        const rateGroups = {};

        for (const [day, rate] of Object.entries(daysWithRate)) {
            // if no rate is set, then skip it
            if (!isNull(rate)) {
                // If the rate group does not exist, create an empty array
                if (!rateGroups[rate]) {
                    rateGroups[rate] = [];
                }
                rateGroups[rate].push(capitalize(day));
            }
        }

        for (const [rate, daysOfWeek] of Object.entries(rateGroups)) {
            // Push the formatted rate band to the result array
            arr.push({
                duration,
                daysOfWeek,
                rate: formatPrice(rate, {
                    formatterCallback: Number,
                    toCents: true,
                }),
            });
        }

        return arr;
    }, []);

    return formattedRateBands;
};

/**
 * Formats rate segments data for an API request by separating the rate segments from the rate triggered segments and formatting the rate values.
 *
 * @param {TFlexRateFormattedRateSegment[]} rateSegments - The rate segments data to be formatted.
 * @returns {{rateSegments: TFlexRateSegment[], rateTriggeredSegments: TFlexRateTriggeredSegment[]}} The formatted rate segments and rate triggered segments data for the API request.
 *
 * @memberof module:api/rates/flex-rates/dto
 * @function formatRateSegmentsRequest
 */
const formatRateSegmentsRequest = (rateSegments) => {
    const formattedRateSegments = rateSegments.reduce(
        (acc, rateSegment) => {
            // handle segments with a trigger type
            if (rateSegment.triggerType) {
                acc.rateTriggeredSegments.push({
                    ...rateSegment,
                    minRate: formatPrice(rateSegment.minRate, {
                        formatterCallback: Number,
                        toCents: true,
                    }),
                    maxRate: formatPrice(rateSegment.maxRate, {
                        formatterCallback: Number,
                        toCents: true,
                    }),
                });
            } else {
                acc.rateSegments.push({
                    ...rateSegment,
                    minRate: formatPrice(rateSegment.minRate, {
                        formatterCallback: Number,
                        toCents: true,
                    }),
                    maxRate: formatPrice(rateSegment.maxRate, {
                        formatterCallback: Number,
                        toCents: true,
                    }),
                });
            }

            return acc;
        },
        {rateSegments: [], rateTriggeredSegments: []}
    );

    return formattedRateSegments;
};

/**
 * Formats the flex rates request
 * @param data
 * @param {TFlexRateFormattedRateBand[]} data.rateBands
 * @param {TFlexRateFormattedRateSegment[]} data.rateSegments
 * @return {TFlexRateConfig}
 *
 * @memberof module:api/rates/flex-rates/dto
 * @function formatSaveFlexRatesRequest
 */
const formatSaveFlexRatesRequest = (data) => {
    const rateBands = formatRateBandsRequest(data.rateBands);
    const {rateSegments, rateTriggeredSegments} = formatRateSegmentsRequest(
        data.rateSegments
    );

    const formattedData = {
        interpolation: {algorithm: capitalize(data.interpolation)},
        // TODO:  Remove this override once the facilities have been migrated over to 30 min configs. @see https://spothero.atlassian.net/browse/SIQ-1263
        durationInMin: FLEX_RATES_CONFIG_DURATION_IN_MIN['30min'],
        rateBands,
        rateSegments,
        rateTriggeredSegments,
    };

    const formattedRequest = convertKeysToSnakeCase(formattedData);

    return formattedRequest;
};

/**
 * Saves and publish flex rates data to the server.
 *
 * @param {Object} data - The flex rates data to be saved.
 * @param {TFlexRateConfig.interpolation} data.interpolation - The interpolation details.
 * @param {TFlexRateFormattedRateBand[]} data.rateBands - The list of rate bands.
 * @param {TFlexRateFormattedRateSegment[]} data.rateSegments - The list of rate segments.
 * @param {number} ruleGroupId - The ID of the rule group to associate the flex rates with.
 * @returns {Promise<TFlexRatesResponse>} A promise that resolves with the formatted flex rates response from the server.
 * @throws {Error} If an error occurs during the process.
 *
 * @see '/api/v1/facilities/rates/rule-groups/{ruleGroupId}/flex-rates/'
 * @function
 */
export const saveFlexRates = async (data, ruleGroupId) => {
    try {
        const formattedRequest = formatSaveFlexRatesRequest(data);

        const response = await spotheroPost(
            `/facilities/rates/rule-groups/${ruleGroupId}/flex-rates/`,
            formattedRequest,
            {},
            false
        );

        const formattedResponse = formatFlexRatesResponse(response?.data?.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        const formattedError = error?.data?.data?.errors?.[0] || error;

        throw formattedError;
    }
};

/**
 * Formats the flex rates matrices response for a 30min config to a format consumable by the heatmap chart
 * @param {TFlexRateMatricesResponse} formattedResponse
 * @param {number[]} xLabels
 * @param {string[]} yLabels
 * @return {TFlexRatesMatrices}
 *
 * @memberof module:api/rates/flex-rates/dto
 * @function formatFlexRatesMatrices30MinResponse
 */
const formatFlexRatesMatrices30MinResponse = (
    formattedResponse,
    xLabels,
    yLabels
) => {
    /** @type {TFlexRatesMatrices} */
    const formattedData = Object.keys(formattedResponse).reduce((acc, day) => {
        const formattedDay = day.toLowerCase();

        // handle the 30 min configs otherwise handle the 1 hour configs
        acc[formattedDay] = formattedResponse[day]
            // filter out the even indices to get the 30 min configs as a default
            .filter((_, i) => i % 2 === 0)
            .map((result, i) => {
                return {
                    id: yLabels[i],
                    data: result
                        // filter out the odd indices to get the 60 min durations since the even indices are the 30 min durations.  This is so we only display rates from the top of each hour (0 index would be 00:30).
                        .filter((_, v) => v % 2 !== 0)
                        .map((value, v) => {
                            return {
                                ...value.source,
                                x: xLabels[v],
                                y: value.rate,
                                searchable: value.searchable,
                            };
                        }),
                };
            });

        return acc;
    }, {});

    return formattedData;
};

/**
 * Formats the flex rates matrices response for a 60min config to a format consumable by the heatmap chart
 * @param formattedResponse
 * @param {number[]} xLabels
 * @param {string[]} yLabels
 * @return {TFlexRatesMatrices}
 *
 * @memberof module:api/rates/flex-rates/dto
 * @function formatFlexRatesMatrices60MinResponse
 */
const formatFlexRatesMatrices60MinResponse = (
    formattedResponse,
    xLabels,
    yLabels
) => {
    const formattedData = Object.keys(formattedResponse).reduce((acc, day) => {
        const formattedDay = day.toLowerCase();

        acc[formattedDay] = formattedResponse[day].map((result, i) => {
            return {
                id: yLabels[i],
                data: result.map((value, v) => {
                    return {
                        ...value.source,
                        x: xLabels[v],
                        y: value.rate,
                        searchable: value.searchable,
                    };
                }),
            };
        });

        return acc;
    }, {});

    return formattedData;
};

/**
 * Formats the flex rates matrices response to a format consumable by the heatmap chart
 * @param {TFlexRateMatricesResponse} response - The response from the API to be formatted
 * @return {TFlexRatesMatrices}
 *
 * @memberof module:api/rates/flex-rates/dto
 * @function formatFlexRatesMatricesResponse
 */
const formatFlexRatesMatricesResponse = (response) => {
    let formattedResponse = ObjectUtils.camelizeKeys(response);
    const {durationInMin} = formattedResponse;

    // strip out the durationInMin key from the response
    formattedResponse = omit(formattedResponse, ['durationInMin']);

    // create an array called xLabels that contain numbers 1-24
    const xLabels = Array.from(Array(24).keys()).map((i) => i + 1);
    // create an array called yLabels that contain hourly durations from 0:00-23:00
    const yLabels = Array.from(Array(24).keys()).map((i) => {
        return dayjs(`${i}`, 'H').format('h:mma');
    });

    // handle the 30 min configs
    if (durationInMin === FLEX_RATES_CONFIG_DURATION_IN_MIN['30min']) {
        return formatFlexRatesMatrices30MinResponse(
            formattedResponse,
            xLabels,
            yLabels
        );
    }

    // handle the 60 min configs as a default
    return formatFlexRatesMatrices60MinResponse(
        formattedResponse,
        xLabels,
        yLabels
    );
};

/**
 * Gets the flex rates matrices for a collection of flex rates
 * @param {number} flexRateId - The id of the flex rate
 * @return {Promise<TFlexRatesMatrices>}
 *
 * @throws {Error}
 *
 * @see '/api/v1/facilities/rates/flex-rates/{flexRateId}/matrices/'
 * @function
 */
export const getFlexRatesMatrices = async (flexRateId) => {
    try {
        const response = await spotheroGet(
            `/facilities/rates/flex-rates/${flexRateId}/matrices/`
        );

        const formattedResponse = formatFlexRatesMatricesResponse(
            response?.data?.data
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Generates a preview for the flex rates matrices for a collection of flex rates
 * @param {Object} data - The flex rates data to be saved.
 * @param {TFlexRateConfig.interpolation} data.interpolation - The interpolation details.
 * @param {TFlexRateFormattedRateBand[]} data.rateBands - The list of rate bands.
 * @param {TFlexRateFormattedRateSegment[]} data.rateSegments - The list of rate segments.
 *
 * @return {Promise<TFlexRatesMatrices>}
 *
 * @see '/api/v1/facilities/rates/flex-rates/generate/'
 * @function
 */
export const previewFlexRatesMatrix = async ({
    rateBands,
    rateSegments,
    interpolation,
}) => {
    try {
        const formattedRequest = formatSaveFlexRatesRequest({
            rateBands,
            rateSegments,
            interpolation,
        });

        const response = await spotheroPost(
            '/facilities/rates/flex-rates/generate/',
            formattedRequest,
            {},
            false
        );

        const formattedResponse = formatFlexRatesMatricesResponse(
            response?.data?.data
        );

        return formattedResponse;
    } catch (error) {
        const formattedError = error?.response.data?.data?.errors?.[0] || error;

        throw formattedError;
    }
};
